import { clone } from "lodash";
import client from "../api/client/SdsWebApiClient";
import { Convert, IntroConfig, Preset } from "../models/modelSelector/IntroConfig";
import { StringPair } from "../api/generated";
import {
    getFrontendSettings as getFrontendSettingsFromConfig,
    getAppVersion as getAppVersionFromConfig,
    getModelFromConfig,
} from "./configUtils";

const defaultIntroConfig: IntroConfig = {
    logo: "",
    stepTexts: [],
    sets: [],
};

export const getConfigFile = async (path: string | undefined | null): Promise<IntroConfig> => {
    if (!path) return defaultIntroConfig;
    const response = await fetch(path, { cache: "no-cache" });
    const config = await response.json();
    return Convert.toIntroConfig(JSON.stringify(config || defaultIntroConfig));
};

const findDefaultPreset = (sets: Preset[]): Preset | undefined => {
    for (const preset of sets) {
        if (preset.default === true) return preset;
        if (preset.sets && preset.sets.length > 0) {
            const found = findDefaultPreset(preset.sets);
            if (found) return found;
        }
    }
    return undefined;
};

/**
 * Resolves the preset to auto-start in when `?start_in_preset=true`. DFS for the first
 * preset flagged `default: true`; falls back to the first top-level preset if none is
 * flagged. Returns `undefined` only when `intro.sets` is empty.
 */
export const findStartInPreset = (intro: IntroConfig): Preset | undefined => {
    return findDefaultPreset(intro.sets) ?? intro.sets[0];
};

export const getStylingFile = async (path: string | undefined | null): Promise<any> => {
    if (!path) return {};
    const response = await fetch(path, { cache: "no-cache" });
    return response.json();
};

export const getApplicationSettings = async (): Promise<StringPair[]> => {
    const settings = await client.getClientSettingsGet();
    if (!settings || !settings.dict) return [];
    return settings.dict;
};

export const getApplicationSetting = (settings: StringPair[], key: string): string => {
    return settings.find(x => x.key === key)?.value || "";
};

export const getFrontendSettings = getFrontendSettingsFromConfig;
export const getAppVersion = getAppVersionFromConfig;

export const getLanguage = async (): Promise<string> => {
    const searchParams = new URLSearchParams(window.location.search);
    const lang = searchParams.get("lang");
    if (lang) return lang.toUpperCase();
    return client.getDefaultLanguageGet();
};

export const getAccountId = async (): Promise<string | null> => {
    const searchParams = new URLSearchParams(window.location.search);
    const account = searchParams.get("account");
    if (account) return account;
    return client.getDefaultSdsAccountGet();
};

export const getImageUrlForConfig = async (
    config: string,
    market: string | undefined,
    serieName: string,
    composedUrl: string,
    width: number,
    height: number,
    accountId: string | null
): Promise<string> => {
    const imagePath = await client.getConfigImageVer2Get(
        config,
        "default",
        market,
        width,
        height,
        0,
        1,
        0,
        0,
        "",
        "",
        "",
        serieName,
        1,
        "jpg",
        accountId || undefined,
        false
    );
    return composedUrl + JSON.parse(imagePath);
};

/**
 * Parses the `?settings=` URL param into a list of model filters. Dedups case-variants
 * (keeping the first-seen case) because `filterSetsRecursive` matches case-insensitively
 * — duplicate variants would otherwise push the same branch multiple times and break the hierarchy.
 */
export const parseSettingsFilter = (settingsParam: string | null): string[] | undefined => {
    if (!settingsParam) return undefined;
    const unique = Array.from(
        new Map(
            settingsParam
                .split(",")
                .map(s => s.trim())
                .filter(s => s.length > 0)
                .map(s => [s.toLowerCase(), s])
        ).values()
    );
    return unique.length > 0 ? unique : undefined;
};

export const filterSetsRecursive = (sets: Preset[], setFilter: string[]): Preset[] => {
    const filteredSets: Preset[] = [];
    sets.forEach(set => {
        let { config, serie } = set;
        const filteredChildSets = set.sets && set.sets.length > 0 ? filterSetsRecursive(set.sets, setFilter) : [];

        const model = getModelFromConfig(config);
        if (!setFilter.some(s => equalsCaseInsensitive(s, model))) {
            if (filteredChildSets.length === 0) return;
            config = filteredChildSets[0].config;
            serie = filteredChildSets[0].serie;
        }

        const filteredSet = clone(set);
        if (filteredChildSets.length > 0) {
            filteredSet.sets = filteredChildSets;
        } else if (filteredSet.sets) {
            delete filteredSet.sets;
        }
        filteredSet.config = config;
        filteredSet.serie = serie;
        filteredSets.push(filteredSet);
    });

    // Order by setFilter, but each preset must appear at most once — duplicate filter
    // entries (or case variants) would otherwise push the same branch multiple times and
    // break the hierarchy (e.g. 3 rooms shown as 6 tiles at the root).
    const orderedSets: Preset[] = [];
    const addedSets = new Set<Preset>();
    setFilter.forEach(filterModel => {
        filteredSets.forEach(set => {
            const model = getModelFromConfig(set.config);
            if (equalsCaseInsensitive(model, filterModel) && !addedSets.has(set)) {
                orderedSets.push(set);
                addedSets.add(set);
            }
        });
    });
    return orderedSets;
};

const equalsCaseInsensitive = (a: string, b: string): boolean =>
    a.localeCompare(b, "en", { sensitivity: "base" }) === 0;

export function findFirstSetByModel(sets: Preset[], models: Set<string>): Preset | null {
    for (const set of sets) {
        if (models.has(getModelFromConfig(set.config))) {
            return set;
        }
    }
    for (const set of sets) {
        if (set.sets) {
            const childSet = findFirstSetByModel(set.sets, models);
            if (childSet) return childSet;
        }
    }
    return null;
}

export const activateArticleNumber = async (
    articleNo: string,
    introConfig: IntroConfig,
    accountId: string | undefined,
    setFilter: string[] | undefined,
    onNavigateToConfig: (configString: string, configEncoding?: string, surfaceProductArticleNo?: string) => void
): Promise<boolean> => {
    try {
        const paths = await client.findMenuPathsByArticleNoGet(articleNo, accountId, true, setFilter);
        if (paths.length === 0) {
            return false;
        }
        const models = new Set(paths.map(p => p.model));
        const set = findFirstSetByModel(introConfig.sets, models);
        if (set) {
            onNavigateToConfig(set.config, undefined, articleNo);
            return true;
        }
        return false;
    } catch {
        return false;
    }
};
